import { type RoleListItem } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { NumberField } from '#client/components/ui/NumberField';
import { Select } from '#client/components/ui/Select';
import { SignatureField } from '#client/components/ui/SignatureField';
import { TextField } from '#client/components/ui/TextField';
import { RESOURCE } from '#client/lib/routes';
import type { SetUserValue, UserFormValues } from './user-form.logic';
import { useAllRecords } from '#client/hooks/useAllRecords';
import { GENDER_OPTIONS } from '#client/lib/form-choices';
import { SIGNATURE_MAX_BYTES } from '#shared/types';

interface FieldsProps {
  values: UserFormValues;
  onFieldChange: SetUserValue;
  errors: Record<string, string>;
}

export function UserAccountCard({ isEdit, ...fields }: FieldsProps & { isEdit: boolean }) {
  return (
    <Card>
      <div className="c-form-grid">
        <PersonFields {...fields} />
        <AccessFields {...fields} isEdit={isEdit} />
      </div>
    </Card>
  );
}

/** Who they are: a fragment inside the account card's grid. */
function PersonFields({ values, onFieldChange, errors }: FieldsProps) {
  return (
    <>
      <TextField
        label="First name"
        value={values.first_name}
        error={errors.first_name}
        onChange={(event) => onFieldChange('first_name', event.target.value)}
        required
      />
      <TextField
        label="Last name"
        value={values.last_name ?? ''}
        error={errors.last_name}
        onChange={(event) => onFieldChange('last_name', event.target.value)}
      />
      <TextField
        label="Father's name"
        value={values.father_name}
        error={errors.father_name}
        onChange={(event) => onFieldChange('father_name', event.target.value)}
        required
      />
      <NumberField
        label="Age"
        min={0}
        max={150}
        value={values.age}
        error={errors.age}
        onChange={(age) => onFieldChange('age', age)}
        required
      />
      <Select
        label="Gender"
        options={GENDER_OPTIONS}
        value={values.gender}
        error={errors.gender}
        onChange={(choice) => onFieldChange('gender', choice)}
        required
      />
    </>
  );
}

/** What they do and how they sign in: also a fragment inside the grid. */
function AccessFields({
  values,
  onFieldChange,
  errors,
  isEdit,
}: FieldsProps & { isEdit: boolean }) {
  return (
    <>
      <RoleSelect
        value={values.role_id}
        error={errors.role_id}
        onChange={(id) => onFieldChange('role_id', id)}
      />
      <TextField
        label="Phone number"
        value={values.phone_number}
        error={errors.phone_number}
        onChange={(event) => onFieldChange('phone_number', event.target.value)}
        required
      />
      <TextField
        label="Email"
        type="email"
        value={values.email}
        error={errors.email}
        onChange={(event) => onFieldChange('email', event.target.value)}
        required
      />
      <TextField
        label="Field"
        value={values.field}
        error={errors.field}
        onChange={(event) => onFieldChange('field', event.target.value)}
        helpText="Speciality or department"
        required
      />
      <TextField
        label={isEdit ? 'New password' : 'Password'}
        type="password"
        autoComplete="new-password"
        value={values.password}
        error={errors.password}
        onChange={(event) => onFieldChange('password', event.target.value)}
        helpText={
          isEdit
            ? 'Leave blank to keep the current password'
            : 'At least 10 characters, with an uppercase letter, a number and a symbol'
        }
        required={!isEdit}
      />
    </>
  );
}

export function ReportSignatureCard({ values, onFieldChange, errors }: FieldsProps) {
  return (
    <Card
      title="Report signature"
      subtitle="How this person appears under a signature block on a printed report"
    >
      <div className="c-form-grid">
        <TextField
          label="Designation"
          value={values.designation ?? ''}
          error={errors.designation}
          onChange={(event) => onFieldChange('designation', event.target.value)}
          helpText="e.g. Senior Lab Technologist. Distinct from Field, which is the bench."
        />
        <TextField
          label="Qualifications"
          value={values.qualifications ?? ''}
          error={errors.qualifications}
          onChange={(event) => onFieldChange('qualifications', event.target.value)}
          helpText="One line, e.g. B.Sc Hons MLT, NIH Isb"
        />
      </div>

      <SignatureField
        label="Signature image"
        value={values.signature_data_url ?? null}
        onChange={(dataUrl) => onFieldChange('signature_data_url', dataUrl)}
        error={errors.signature_data_url}
        helpText={`Optional. PNG or JPEG under ${SIGNATURE_MAX_BYTES / 1024} KB.`}
      />
    </Card>
  );
}

/**
 * The roles the lab has defined, by name.
 *
 * Read from the server rather than a fixed list, because the lab makes its own.
 * The server also refuses a role wider than the person assigning it, and says
 * so; listing it anyway keeps the dropdown honest about what exists.
 */
function RoleSelect({
  value,
  error,
  onChange,
}: {
  value: number;
  error: string | undefined;
  onChange: (id: number) => void;
}) {
  const roles = useAllRecords<RoleListItem>({ path: RESOURCE.roles });
  const options = roles.items.map((role) => ({
    value: String(role.id),
    label: role.is_super ? `${role.name} (full access)` : role.name,
  }));
  return (
    <Select
      label="Role"
      placeholder={roles.loading ? 'Loading roles…' : 'Choose a role'}
      options={options}
      value={value ? String(value) : ''}
      error={error ?? roles.error}
      onChange={(choice: string) => onChange(choice === '' ? 0 : Number(choice))}
      required
    />
  );
}
